import { ERROR_MESSAGES } from "../../../shared/errors";
import { allowedSchemaTypes } from "../../config";
import { EnvxError, EnvxSchema, EnvxVarSchema } from "../../types";

import {
  isSafeString,
  assertNoTrailingPunctuation,
  applyDefault,
  splitKeyValue,
  stripQuotes,
} from "./helpers";
import { VALID_SCHEMA_KEY_REGEX } from "../../regex";

/**
 * Parses a line inside a schema block and applies it to the given schema variable.
 *
 * Supported keys: type, required, default, values
 *
 * @param {string} line - The line inside the schema block.
 * @param {number} lineNumber - Current line number (zero-based).
 * @param {EnvxVarSchema} target - Target schema object for this variable.
 *
 * @throws {EnvxError} For unknown keys or invalid value formats.
 */
function parseSchemaLine(
  line: string,
  lineNumber: number,
  target: EnvxVarSchema & { __pendingDefault?: string },
  getNextLine?: () => string | null
) {
  const [rawKey, rawValue] = splitKeyValue(line, lineNumber);
  const key = rawKey.trim();
  const value: any = stripQuotes(rawValue.trim());

  if (key === "description" && rawValue.trim().startsWith('"""')) {
    let descLines = [];
    let currentLine = rawValue.trim().slice(3);

    if (currentLine.endsWith('"""')) {
      target.description = currentLine.slice(0, -3);
      return;
    }

    descLines.push(currentLine);

    while (getNextLine) {
      const nextLine = getNextLine();
      if (nextLine === null) {
        throw new EnvxError(
          `Line ${lineNumber + 1}: Unterminated multiline description.`
        );
      }
      if (nextLine.endsWith('"""')) {
        descLines.push(nextLine.slice(0, -3));
        break;
      } else {
        descLines.push(nextLine);
      }
    }

    target.description = descLines.join("\n");
    return;
  }

  switch (key) {
    case "required":
      target.required = value === "true";
      break;

    case "type":
      if (target.type !== undefined) {
        // [THROW_ERR_MSG] => Throw if "type" is defined more than once for a schema
        throw new EnvxError(
          ERROR_MESSAGES.lib.parser.typeAlreadyDefined(lineNumber)
        );
      }
      if (!allowedSchemaTypes.includes(value)) {
        throw new EnvxError(
          ERROR_MESSAGES.lib.unsupportedType(lineNumber, value)
        );
      }
      target.type = value as EnvxVarSchema["type"];
      break;

    case "default":
      if (target.type == "enum" && !target.values) {
        target.__pendingDefault = value;
      } else {
        applyDefault(value, lineNumber, target);
      }
      break;

    case "values":
      try {
        const parsed = JSON.parse(value);

        const isValidArray =
          Array.isArray(parsed) &&
          parsed.length <= 25 &&
          parsed.every((v) => typeof v === "string" && isSafeString(v));

        if (!isValidArray) {
          throw new EnvxError(
            `[envx:error] Line ${
              lineNumber + 1
            }: "values" must be a JSON array (max 25) of safe strings. Allowed characters: a-z, A-Z, 0-9, _, -, .`
          );
        }

        target.values = parsed;

        if (target.__pendingDefault) {
          applyDefault(target.__pendingDefault, lineNumber, target);
          // delete (target as any).__pendingDefault;
        }
      } catch (e) {
        // [THROW_ERR_MSG] => Throw if "values" is not a valid JSON array of strings
        throw new EnvxError(
          ERROR_MESSAGES.lib.parser.invalidValuesJson(lineNumber)
        );
      }
      break;

    // Note: 'deprecated' and 'description' are mostly for editor tooling (like VSCode extensions),
    // they don't affect runtime behavior directly.
    case "deprecated":
      target.deprecated = value;
      break;
    case "description":
      target.description = value;
      break;

    default:
      // [THROW_ERR_MSG] => Throws error if a schema property is unknown.
      throw new EnvxError(
        ERROR_MESSAGES.lib.parser.unknownSchemaProperty(lineNumber, key)
      );
  }
}

function parseSchemaHeader(
  line: string,
  lineNumber: number,
  schema: EnvxSchema
): string {
  const key = line.slice(1, -1).trim();

  if (!key) {
    // [THROW_ERR_MSG] => Throws error if a schema block name is empty.
    throw new EnvxError(ERROR_MESSAGES.lib.parser.schemaBlockEmpty(lineNumber));
  }

  if (!VALID_SCHEMA_KEY_REGEX.test(key)) {
    throw new EnvxError(
      `[envx:error] Line ${lineNumber + 1}: ` +
        `Schema block name "${key}" contains invalid characters. ` +
        `Only letters, numbers, underscore (_), hyphen (-), and dot (.) are allowed.`
    );
  }

  if (schema[key]) {
    // [THROW_ERR_MSG] => Throws error if a schema key is duplicated.
    throw new EnvxError(
      ERROR_MESSAGES.lib.parser.schemaKeyDuplicate(lineNumber, key)
    );
  }

  schema[key] = schema[key] ?? {};
  return key;
}

function parseEnvLine(
  line: string,
  lineNumber: number
): {
  key: string;
  value: string;
  isMultilineStart: boolean;
} {
  const [rawKey, rawValue] = splitKeyValue(line, lineNumber);
  const key = rawKey.trim();

  if (!key) {
    // [THROW_ERR_MSG] => Throws error if environment variable name is empty.
    throw new EnvxError(
      ERROR_MESSAGES.lib.parser.variableNameEmpty(lineNumber)
    );
  }

  const trimmedValue = rawValue.trim();

  if (trimmedValue.startsWith('"""')) {
    if (trimmedValue.endsWith('"""') && trimmedValue.length > 6) {
      return {
        key,
        value: trimmedValue.slice(3, -3),
        isMultilineStart: false,
      };
    }

    return {
      key,
      value: trimmedValue.slice(3),
      isMultilineStart: true,
    };
  }
  assertNoTrailingPunctuation(trimmedValue, lineNumber);

  return {
    key,
    value: stripQuotes(trimmedValue),
    isMultilineStart: false,
  };
}

export { parseEnvLine, parseSchemaHeader, parseSchemaLine };
